import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import {
  TrendingUp,
  TrendingDown,
  Eye,
  Users,
} from 'lucide-react';

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import analyticsService, { SellerAnalyticsData } from '../../../services/analytics.service';
import { Seo } from '../../components/Seo';

function AnalyticsSkeleton() {
  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <Skeleton className="h-8 w-44 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="bg-white rounded-2xl p-5 border border-[#E5E7EB]"
          >
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="h-4 w-14" />
            </div>

            <Skeleton className="h-8 w-24 mb-2" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Area Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <div className="flex justify-between mb-5">
            <Skeleton className="h-6 w-48" />
            <div className="flex gap-3">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
          <Skeleton className="h-[220px] w-full rounded-xl" />
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <Skeleton className="h-6 w-40 mb-5" />
          <div className="flex justify-center mb-5">
            <Skeleton className="w-40 h-40 rounded-full" />
          </div>
          {[1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="flex justify-between items-center mb-3"
            >
              <div className="flex items-center gap-2">
                <Skeleton className="w-3 h-3 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      </div>

      {/* Listing Performance */}
      <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
        <Skeleton className="h-6 w-44 mb-5" />
        {[1, 2, 3].map((item) => (
          <div key={item} className="mb-5">
            <div className="flex justify-between mb-2">
              <Skeleton className="h-4 w-40" />
              <div className="flex gap-4">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
const TRAFFIC_SOURCES = [
  { name: 'Direct Search', value: 40, color: '#1E6DFF' },
  { name: 'Homepage', value: 25, color: '#22C55E' },
  { name: 'WhatsApp Share', value: 20, color: '#F59E0B' },
  { name: 'Social Media', value: 15, color: '#BFC2C7' },
];

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

export function Analytics() {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SellerAnalyticsData | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAnalytics = async () => {
      try {
        const res = await analyticsService.getSellerAnalytics();
        if (isMounted && res.success && res.data) {
          setData(res.data);
        }
      } catch (err) {
        console.error("Failed to load seller analytics:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return <AnalyticsSkeleton />;
  }

  const kpis = data?.kpis || {
    totalViews: 0,
    totalLeads: 0,
    leadRate: "0.0%",
    activeListings: 0,
    totalListings: 0,
  };

  const monthly = data?.monthly || [];
  const listingPerf = data?.listingPerformance || [];
  const trafficSources = data?.trafficSources && data.trafficSources.length > 0
    ? data.trafficSources
    : [];

  const maxViews = Math.max(...listingPerf.map((l) => l.views), 1);

  return (
    <>
     <Seo
      title="Seller Analytics | Morrocars"
      description="Track your vehicle listing performance, views, leads, traffic sources and seller analytics on Morrocars."
    />
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">{t('analytic.title')}</h1>
        <p className="text-[#717182]">{t('analytic.subtitle')}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Total Views',
            value: data !== null ? Number(kpis.totalViews).toLocaleString() : '1,455',
            change: kpis.viewsChange?.change ?? '+0%',
            up: kpis.viewsChange?.up ?? true,
            icon: Eye,
          },
          {
            label: 'Total Leads',
            value: data !== null ? Number(kpis.totalLeads).toLocaleString() : '52',
            change: kpis.leadsChange?.change ?? '+0%',
            up: kpis.leadsChange?.up ?? true,
            icon: Users,
          },
          {
            label: 'Avg. Lead Rate',
            value: data !== null ? kpis.leadRate : '3.6%',
            change: kpis.rateChange?.change ?? '+0%',
            up: kpis.rateChange?.up ?? true,
            icon: TrendingUp,
          },
          {
            label: 'Avg. Response Time',
            value: data !== null ? (kpis.avgResponseTime?.value || '< 1h') : '1.2h',
            change: kpis.avgResponseTime?.change ?? '0.0h',
            up: kpis.avgResponseTime?.up ?? true,
            icon: TrendingUp,
          },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#1E6DFF]" />
                </div>
                <span className={`text-xs flex items-center gap-0.5 ${s.up ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                  {s.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {s.change}
                </span>
              </div>
              <p className="text-2xl text-[#2D323A]">{s.value}</p>
              <p className="text-xs text-[#717182]">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#2D323A] font-medium">{t('analytic.charts.viewsLeadsOverTime')}</h3>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1 text-[#1E6DFF]">
                <span className="w-3 h-0.5 bg-[#1E6DFF] inline-block" />
                {t('analytic.charts.viewsLabel')}
              </span>
              <span className="flex items-center gap-1 text-[#22C55E]">
                <span className="w-3 h-0.5 bg-[#22C55E] inline-block" />
                {t('analytic.charts.leadsLabel')}
              </span>
            </div>
          </div>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly}>
                <defs>
                  <linearGradient id="viewsG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E6DFF" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#1E6DFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
                <Area type="monotone" dataKey="views" stroke="#1E6DFF" strokeWidth={2} fill="url(#viewsG)" dot={{ r: 3, fill: '#1E6DFF' }} />
                <Area type="monotone" dataKey="leads" stroke="#22C55E" strokeWidth={2} fill="none" dot={{ r: 3, fill: '#22C55E' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-[#717182]">
              No view or lead history recorded yet.
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">{t('analytic.trafficSources.title')}</h3>
          {trafficSources.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={trafficSources} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                    {trafficSources.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {trafficSources.map(s => (
                  <div key={s.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-[#717182]">{s.name}</span>
                    </div>
                    <span className="text-[#2D323A]">{s.value}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-44 flex flex-col items-center justify-center text-center text-[#717182] text-sm">
              <span className="text-2xl mb-1">📊</span>
              <span>No traffic data recorded yet</span>
            </div>
          )}
        </div>
      </div>
      {/* Listing Performance */}
      <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
        <h3 className="text-[#2D323A] font-medium mb-4">
          {t('analytic.listingPerformance.title')}
        </h3>

        {listingPerf.length > 0 ? (
          <div className="space-y-4">
            {listingPerf.map((l) => (
              <div key={l.id || l.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-[#2D323A] font-medium">
                    {l.name}
                  </span>

                  <div className="flex gap-4 text-sm text-[#717182]">
                    <span className="flex items-center gap-1" title="Views">
                      <Eye className="w-3.5 h-3.5" />
                      {Number(l.views).toLocaleString()}
                    </span>

                    <span className="flex items-center gap-1" title="Leads">
                      <Users className="w-3.5 h-3.5" />
                      {Number(l.leads).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1E6DFF] rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(5, (l.views / maxViews) * 100)
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-[#717182]">
            No listings created yet. Create a vehicle listing to see performance insights here.
          </div>
        )}
      </div>
    </div >
    </>
  );
}

export default Analytics;
